<template>
  <div class="w-full p-6 space-y-10">
    <div class="w-full border rounded-lg p-6 border-slate-200 bg-white">
      <div class="flex w-full gap-8 pb-6">
        <div class="w-1/4 space-y-1">
          <h3 class="font-semibold text-lg text-dark-neutral-500">Textual Synthesis</h3>
          <p class="text-sm text-gray-500 font-normal">
            Enable synthesis to analyze and summarize extracted data across studies.
          </p>
        </div>
        <div class="w-full space-y-4">
          <SectionCard class="w-full" title="Enable textual synthesis">
            <template #header-button>
              <PlanLockGuard>
                <Switch
                  aria-label="Enable textual synthesis"
                  :disabled="review.isPlanReadonly.value"
                  :model-value="review.entity.value?.plan?.synthesisPlan?.isTextualSynthesisEnabled"
                  @update:model-value="toggleSynthesis"
                >
                  <template #icon>
                    <component
                      :is="
                        review.entity.value?.plan?.synthesisPlan?.isTextualSynthesisEnabled
                          ? ArrowsPointingInIcon
                          : ArrowsPointingOutIcon
                      "
                      class="w-3 h-auto"
                      :class="
                        review.entity.value?.plan?.synthesisPlan?.isTextualSynthesisEnabled
                          ? 'text-green-500'
                          : 'text-dark-neutral-100'
                      "
                    />
                  </template>
                </Switch>
              </PlanLockGuard>
            </template>

            <div
              v-if="review.entity.value?.plan?.synthesisPlan?.isTextualSynthesisEnabled"
              class="space-y-2"
            >
              <h3 class="text-dark-neutral-500 text-sm font-semibold">Synthesis methodology</h3>
              <PlanLockGuard>
                <TextInput
                  v-model="textualSynthesisDescription"
                  :placeholder="`Describe the synthesis approach and methodology`"
                  type="text"
                  :multiline="true"
                  :can-resize="true"
                  input-classes="w-full pl-5 py-5 min-h-[200px] text-sm hover:border-mid-neutral-50 focus:border-mid-neutral-50"
                  :disabled="review.isPlanReadonly.value"
                  @enter="(v) => updateTextualSynthesisDescription(v)"
                  @blur="(v) => updateTextualSynthesisDescription(v)"
                />
              </PlanLockGuard>
            </div>
          </SectionCard>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import Switch from "@app/components/Global/Switch.vue";
import { injectStrict } from "@app/utils/injectStrict";
import { ReviewKey } from "@app/views/Review/use-review";
import { HttpException } from "@core/exceptions/http.exception";
import useSnackbar from "@app/composables/use-snackbar";
import { SnackbarState } from "@app/types";
import useLoading from "@app/composables/use-loading";
import SectionCard from "@app/components/Global/SectionCard.vue";
import TextInput from "@app/components/Global/Inputs/TextInput.vue";
import { ArrowsPointingOutIcon, ArrowsPointingInIcon } from "@heroicons/vue/24/outline";
import PlanLockGuard from "../PlanLockGuard.vue";
import { ref } from "vue";
import { errorMessage } from "@app/utils/error-message";

const review = injectStrict(ReviewKey);
const snackbar = useSnackbar();
const loading = useLoading();
const textualSynthesisDescription = ref(
  review.entity.value.plan?.synthesisPlan.textualSynthesisDescription,
);

async function toggleSynthesis() {
  loading.start();
  try {
    if (review.entity.value?.plan?.synthesisPlan?.isTextualSynthesisEnabled) {
      await review.disableTextualSynthesis();
      snackbar.success("Success");
    } else {
      await review.enableTextualSynthesis();
      snackbar.success("Success");
    }
  } catch (e) {
    const error = e as HttpException;
    if (error.response.data.statusCode >= 400 && error.response.data.statusCode < 500) {
      snackbar.show(SnackbarState.ERROR, error.response.data.message);
    } else {
      snackbar.show(
        SnackbarState.ERROR,
        "An error occurred while enabling/disabling synthesis, please try again.",
      );
    }
    throw e;
  } finally {
    loading.stop();
  }
}

async function updateTextualSynthesisDescription(newDescription: string) {
  try {
    loading.start();
    await review.updateTextualSynthesisDescription(newDescription);
    snackbar.success("Success");
  } catch (e) {
    const error = e as HttpException;
    snackbar.show(SnackbarState.ERROR, errorMessage(error.response.data));
    if (error.response.data.statusCode >= 500) {
      throw e;
    }
  } finally {
    loading.stop();
  }
}
</script>
